import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../shared/api";
import { normalizeNumberInput } from "../shared/format";

// Внимание: product.cost_price — это розничная цена (легаси-название на бэкенде),
// себестоимость — product.self_cost_price.
const PRICE_FIELDS = [
  { key: "self_cost_price", label: "Себестоимость", directorOnly: false },
  { key: "cost_price", label: "Розничная цена", directorOnly: true },
  { key: "wholesale_price", label: "Оптовая цена", directorOnly: true },
];

function formatMoney(value) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function toFormValue(value) {
  return value == null ? "" : String(value);
}

/**
 * Окно редактирования цен товара с историей изменений.
 *
 * Поля, недоступные по роли, показываются заблокированными, а не прячутся —
 * значение должно быть видно и без права на правку.
 */
export default function ProductPricesModal({
  open,
  product,
  itemId,
  requestId,
  canEditSelfCost = false,
  canEditAllPrices = false,
  onCancel,
  onSaved,
}) {
  const [form, setForm] = useState({ self_cost_price: "", cost_price: "", wholesale_price: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyStatus, setHistoryStatus] = useState("idle");

  const productId = product?.id ?? null;

  const initial = useMemo(
    () => ({
      self_cost_price: toFormValue(product?.self_cost_price),
      cost_price: toFormValue(product?.cost_price),
      wholesale_price: toFormValue(product?.wholesale_price),
    }),
    [product?.self_cost_price, product?.cost_price, product?.wholesale_price]
  );

  useEffect(() => {
    if (!open) return;
    setForm(initial);
    setError(null);
  }, [open, initial]);

  const loadHistory = useCallback(async () => {
    if (productId == null) return;
    setHistoryStatus("loading");
    try {
      const data = await apiFetch(
        `/api/product/product-cost-history/?product=${encodeURIComponent(productId)}&ordering=-changed_at&page_size=50`,
        { method: "GET" }
      );
      const rows = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
      setHistory(rows);
      setHistoryStatus("succeeded");
    } catch (err) {
      setHistory([]);
      setHistoryStatus("failed");
    }
  }, [productId]);

  useEffect(() => {
    if (!open) return;
    loadHistory();
  }, [open, loadHistory]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onCancel]);

  const canEditField = useCallback(
    (fieldDef) => (fieldDef.directorOnly ? canEditAllPrices : canEditSelfCost),
    [canEditAllPrices, canEditSelfCost]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Отправляем только реально изменённые поля: иначе история заполнится
    // строками по нетронутым ценам.
    const body = {};
    for (const fieldDef of PRICE_FIELDS) {
      if (!canEditField(fieldDef)) continue;
      const normalized = normalizeNumberInput(form[fieldDef.key]);
      if (normalized == null) continue;
      if (normalized === normalizeNumberInput(initial[fieldDef.key])) continue;
      const num = Number(normalized);
      if (!Number.isFinite(num) || num < 0) {
        setError(`${fieldDef.label}: должно быть числом >= 0`);
        return;
      }
      body[fieldDef.key] = normalized;
    }

    if (Object.keys(body).length === 0) {
      onCancel();
      return;
    }

    setSaving(true);
    try {
      await apiFetch(`/api/product/purchase-requests/${requestId}/items/${itemId}/cost-price/`, {
        method: "PATCH",
        body,
      });
      setSaving(false);
      onSaved(productId);
    } catch (err) {
      setSaving(false);
      const msg =
        (err?.data && typeof err.data === "object" && (err.data.error || err.data.detail || err.data.message)) ||
        err?.message ||
        "Не удалось сохранить цены";
      setError(msg);
    }
  };

  if (!open) return null;

  return (
    <div style={styles.overlay} onMouseDown={onCancel}>
      <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>Цены — {product?.name || "товар"}</div>
          <button type="button" style={styles.modalClose} onClick={onCancel} aria-label="Закрыть">
            ×
          </button>
        </div>

        <form style={styles.modalBody} onSubmit={handleSubmit}>
          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.priceRow}>
            {PRICE_FIELDS.map((fieldDef) => {
              const editable = canEditField(fieldDef);
              return (
                <div key={fieldDef.key} style={styles.field}>
                  <label style={styles.label} htmlFor={`price-${fieldDef.key}`}>
                    {fieldDef.label}
                  </label>
                  <input
                    id={`price-${fieldDef.key}`}
                    style={{ ...styles.input, ...(editable ? null : styles.inputDisabled) }}
                    value={form[fieldDef.key]}
                    onChange={(e) => setForm((p) => ({ ...p, [fieldDef.key]: e.target.value }))}
                    disabled={!editable}
                    title={editable ? undefined : "Эту цену может менять только директор"}
                    inputMode="decimal"
                  />
                </div>
              );
            })}
          </div>

          <div style={styles.hint}>
            Цены меняются в карточке товара — значения применятся во всех заявках.
          </div>

          <div style={styles.historyTitle}>История изменений</div>
          {historyStatus === "loading" && <div style={styles.muted}>Загружаю…</div>}
          {historyStatus === "failed" && <div style={styles.muted}>Не удалось загрузить историю</div>}
          {historyStatus === "succeeded" && history.length === 0 && (
            <div style={styles.muted}>Изменений пока нет</div>
          )}
          {history.length > 0 && (
            <div style={styles.historyWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Дата</th>
                    <th style={styles.th}>Цена</th>
                    <th style={styles.th}>Было → стало</th>
                    <th style={styles.th}>Кто</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.id}>
                      <td style={styles.td}>{formatDateTime(row.changed_at)}</td>
                      <td style={styles.td}>{row.field_display || row.field}</td>
                      <td style={styles.td}>
                        {formatMoney(row.old_value)} → {formatMoney(row.new_value)}
                      </td>
                      <td style={styles.td}>{row.changed_by_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={styles.modalActions}>
            <button type="button" style={styles.cancelBtn} onClick={onCancel} disabled={saving}>
              Отмена
            </button>
            <button type="submit" style={styles.confirmBtn} disabled={saving}>
              {saving ? "Сохраняю…" : "Сохранить"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 1000,
  },
  modal: {
    width: "min(720px, 94vw)",
    maxHeight: "90vh",
    background: "var(--c-card)",
    border: "1px solid var(--c-border-light)",
    borderRadius: 16,
    boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    padding: "14px 20px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  modalTitle: { fontSize: 17, fontWeight: 700, color: "var(--c-text)" },
  modalClose: {
    width: 30,
    height: 30,
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontSize: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalBody: { padding: 20, overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 14 },
  field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  priceRow: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 },
  label: { fontSize: 14, fontWeight: 600, color: "var(--c-text-muted)" },
  input: {
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    fontFamily: "inherit",
  },
  inputDisabled: { opacity: 0.55, cursor: "not-allowed" },
  hint: { fontSize: 14, color: "var(--c-text-muted)" },
  historyTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "var(--c-text)",
    borderTop: "1px solid var(--c-border)",
    paddingTop: 14,
  },
  muted: { fontSize: 14, color: "var(--c-text-muted)" },
  historyWrap: { overflow: "auto", maxHeight: 260 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    color: "var(--c-text-muted)",
    borderBottom: "1px solid var(--c-border)",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 10px",
    color: "var(--c-text)",
    borderBottom: "1px solid var(--c-border)",
    whiteSpace: "nowrap",
  },
  error: {
    padding: "10px 12px",
    borderRadius: 8,
    background: "var(--c-danger-dim)",
    color: "var(--c-danger)",
    border: "1px solid var(--c-danger)",
    fontSize: 15,
    fontWeight: 700,
  },
  modalActions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 },
  confirmBtn: {
    padding: "9px 20px",
    borderRadius: 99,
    border: "none",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
  },
  cancelBtn: {
    padding: "9px 20px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 15,
  },
};
